/**
 * ── Pricing Engine ──────────────────────────────────────────────────────────
 *
 * Shared, pure functions for the DCC banquet booking pricing system.
 *
 * Two item pools feed into the total:
 *   1. Module line items  (hall, food, bar, LED, addons) — adjustable by default
 *   2. Vendor service items — NOT adjustable by default (unless vendor config says so)
 *
 * A discount is distributed proportionally across **adjustable** items only.
 * Each item's min_price_limit is enforced; any "leftover" discount that can't
 * be applied (because an item already hit its floor) is NOT redistributed — the
 * discount simply absorbs less than requested. This keeps the math predictable
 * and avoids infinite redistribution loops.
 */

import { BookingFormState, VendorServiceItem, n } from "./bookingTypes";

// ── Types ────────────────────────────────────────────────────────────────────

export type PriceStatus = "discounted" | "increased" | "fixed";

export interface PricingItem {
  id: string | number;
  label: string;
  base_price: number;
  final_price: number;
  is_adjustable: boolean;
  min_price_limit: number | null;
  price_status: PriceStatus;
}

export interface DiscountResult {
  items: PricingItem[];
  totalBefore: number;
  totalAfter: number;
  discountApplied: number;
  discountRequested: number;
}

// ── Core: applyDiscount ──────────────────────────────────────────────────────

/**
 * Distributes a discount proportionally across adjustable items.
 *
 * @param items     - All pricing line items (modules + vendor services).
 * @param discount  - The total dollar amount to remove. Positive = discount,
 *                    negative = surcharge (prices increase).
 * @returns         - New items array with updated final_price & price_status.
 */
export function applyDiscount(
  items: PricingItem[],
  discount: number,
): DiscountResult {
  const totalBefore = items.reduce((s, i) => s + i.base_price, 0);

  // Nothing to adjust → return originals as fixed
  if (discount === 0) {
    const out = items.map((item) => ({
      ...item,
      final_price: item.is_adjustable ? item.base_price : item.base_price,
      price_status: (item.is_adjustable ? "discounted" : "fixed") as PriceStatus,
    }));
    // If no discount, everything is at base — just mark correctly
    return {
      items: out.map((i) => ({ ...i, price_status: i.is_adjustable ? "discounted" : "fixed", final_price: i.base_price })),
      totalBefore,
      totalAfter: totalBefore,
      discountApplied: 0,
      discountRequested: 0,
    };
  }

  const adjustable = items.filter((i) => i.is_adjustable);
  const adjustableTotal = adjustable.reduce((s, i) => s + i.base_price, 0);

  // No adjustable items → can't apply any discount
  if (adjustableTotal === 0) {
    const out = items.map((item) => ({
      ...item,
      final_price: item.base_price,
      price_status: "fixed" as PriceStatus,
    }));
    return {
      items: out,
      totalBefore,
      totalAfter: totalBefore,
      discountApplied: 0,
      discountRequested: discount,
    };
  }

  // Proportional ratio: how much of each item's base_price to remove
  const ratio = Math.min(discount / adjustableTotal, 1); // cap at 100%

  let discountApplied = 0;

  const result = items.map((item): PricingItem => {
    // Fixed items pass through untouched
    if (!item.is_adjustable) {
      return {
        ...item,
        final_price: item.base_price,
        price_status: "fixed",
      };
    }

    let newPrice = item.base_price - item.base_price * ratio;

    // Enforce minimum price limit
    if (item.min_price_limit !== null && newPrice < item.min_price_limit) {
      newPrice = item.min_price_limit;
    }

    // Round to 2 decimal places
    newPrice = Math.round(newPrice * 100) / 100;

    // Determine status
    let status: PriceStatus = "discounted";
    if (newPrice > item.base_price) {
      status = "increased";
    } else if (newPrice === item.base_price) {
      status = "fixed";
    }

    discountApplied += item.base_price - newPrice;

    return {
      ...item,
      final_price: newPrice,
      price_status: status,
    };
  });

  discountApplied = Math.round(discountApplied * 100) / 100;
  const totalAfter = result.reduce((s, i) => s + i.final_price, 0);

  return {
    items: result,
    totalBefore,
    totalAfter: Math.round(totalAfter * 100) / 100,
    discountApplied,
    discountRequested: discount,
  };
}

// ── Helper: Build pricing items from booking form ────────────────────────────

export interface ModuleLineItem {
  key: string;
  label: string;
  basePrice: number;
  isAdjustable: boolean;
  minPriceLimit: number | null;
}

/**
 * Extracts module line items from the booking form.
 * Modules (hall, food, bar, LED, addons) are adjustable by default.
 */
export function buildModuleItems(form: BookingFormState): ModuleLineItem[] {
  const guests = n(form.guestCount);
  const items: ModuleLineItem[] = [];

  // Hall
  const hallBase = n(form.hallFlatFee) + n(form.hallPricePerPerson) * guests;
  if (hallBase > 0) {
    items.push({ key: "hall", label: "Hall Rental", basePrice: hallBase, isAdjustable: true, minPriceLimit: null });
  }

  // Food
  const foodBase = (n(form.foodServicePricePerPerson) + n(form.menuPricePerPerson)) * guests;
  if (foodBase > 0) {
    items.push({ key: "food", label: "Food & Menu", basePrice: foodBase, isAdjustable: true, minPriceLimit: null });
  }

  // Bar
  const barBase = n(form.barServiceAmount)
    + (n(form.bartenderCount) * n(form.bartenderAmount))
    + n(form.barPackagePricePerPerson) * guests;
  if (barBase > 0) {
    items.push({ key: "bar", label: "Bar Service", basePrice: barBase, isAdjustable: true, minPriceLimit: null });
  }

  // LED
  const ledBase = form.selectedLEDs.reduce((s, led) => {
    const prices: Record<string, number> = { "LED 1": 3000, "LED 2": 3000, "LED 3": 1800, "LED 4": 1800 };
    return s + (prices[led] ?? 0);
  }, 0);
  if (ledBase > 0) {
    items.push({ key: "led", label: "LED Screens", basePrice: ledBase, isAdjustable: true, minPriceLimit: null });
  }

  // Add-ons (each addon is a line item)
  form.addons.forEach((a, i) => {
    if (a.lineTotal > 0) {
      items.push({
        key: `addon-${a.service_id ?? i}`,
        label: a.itemName,
        basePrice: a.lineTotal,
        isAdjustable: true,
        minPriceLimit: null,
      });
    }
  });

  return items;
}

/**
 * Calculate base price from rate using pricing_type rules:
 *   per_event  → dcc_price (flat)
 *   per_person → guestCount × dcc_price
 *   per_plate  → guestCount × dcc_price
 *   per_hour   → hours × dcc_price
 *   per_unit   → quantity × dcc_price
 */
export function calculateItemBasePrice(
  rate: number,
  quantity: number,
  hours: number,
  pricingType: string,
): number {
  switch (pricingType) {
    case "per_event":  return rate;
    case "per_person": return rate * quantity;
    case "per_plate":  return rate * quantity;
    case "per_hour":   return rate * hours;
    case "per_unit":   return rate * quantity;
    default:           return rate * quantity;
  }
}

/**
 * Converts vendor services into PricingItem[].
 * Vendor services inherit is_adjustable from vendor config (default false).
 */
export function buildVendorItems(vendorServices: VendorServiceItem[]): PricingItem[] {
  return vendorServices.map((vs) => {
    const basePrice = calculateItemBasePrice(vs.rate, vs.quantity, vs.hours, vs.pricingType);

    return {
      id: `vendor-${vs.itemId}`,
      label: `${vs.itemName} (${vs.vendorName})`,
      base_price: Math.round(basePrice * 100) / 100,
      final_price: Math.round(basePrice * 100) / 100,
      is_adjustable: vs.isAdjustable,
      min_price_limit: vs.minPriceLimit,
      price_status: vs.isAdjustable ? "discounted" : "fixed",
    };
  });
}

/**
 * Converts module line items into PricingItem[].
 */
export function buildModulePricingItems(modules: ModuleLineItem[]): PricingItem[] {
  return modules.map((m) => ({
    id: m.key,
    label: m.label,
    base_price: Math.round(m.basePrice * 100) / 100,
    final_price: Math.round(m.basePrice * 100) / 100,
    is_adjustable: m.isAdjustable,
    min_price_limit: m.minPriceLimit,
    price_status: m.isAdjustable ? "discounted" : "fixed",
  }));
}

/**
 * Full pricing calculation: combines modules + vendor services,
 * applies discount, and returns the complete result.
 */
export function calculatePricing(
  form: BookingFormState,
  discount: number,
): DiscountResult {
  const moduleItems = buildModulePricingItems(buildModuleItems(form));
  const vendorItems = buildVendorItems(form.vendorServices);
  const allItems = [...moduleItems, ...vendorItems];

  return applyDiscount(allItems, discount);
}
